// -----------------------------------------------------------------------------
// Browser web-push client (Firebase Cloud Messaging).
//
// GATED: while `isWebPushConfigured` is false (placeholders still in
// src/config/firebaseWeb.ts) this module registers NO service worker, imports
// NO Firebase SDK, and simply surfaces the native permission prompt so the
// Settings "Enable" toggle still behaves. Once the config is filled it activates
// with no code change. See WEB_PUSH_SETUP.md.
// -----------------------------------------------------------------------------

import {
  firebaseWebConfig,
  VAPID_KEY,
  isWebPushConfigured,
} from '../config/firebaseWeb';
import { updateWebPushToken } from '../api/General/account';

const SW_URL = '/firebase-messaging-sw.js';

// Idempotence guards — a repeated "Enable" click must not re-register or
// re-POST an unchanged token, nor bind the foreground handler twice.
let lastRegisteredToken: string | null = null;
let foregroundHandlerBound = false;

async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * Enable browser web push. Safe to call repeatedly (idempotent).
 *
 * - Not configured yet → warn + native permission prompt fallback, no Firebase.
 * - Configured → register the SW, init Firebase messaging (guarded against
 *   double-init), request permission, fetch the FCM token, POST it to the
 *   backend, and bind a foreground `onMessage` handler.
 *
 * Never throws — all failures are logged and swallowed so the caller (the
 * Settings toggle) can always resolve.
 */
export async function enableWebPush(): Promise<void> {
  if (!isWebPushConfigured) {
    console.warn(
      '[web-push] Firebase Web push is not configured yet — see mowazaf.client/WEB_PUSH_SETUP.md. ' +
        'Falling back to a bare browser notification prompt.',
    );
    await requestNotificationPermission();
    return;
  }

  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      console.warn('[web-push] Service workers are not supported in this browser.');
      await requestNotificationPermission();
      return;
    }

    // Lazy-load the Firebase SDK only when actually configured, so an
    // unconfigured build never pulls it into the runtime path.
    const { initializeApp, getApps, getApp } = await import('firebase/app');
    const { getMessaging, getToken, onMessage, isSupported } = await import(
      'firebase/messaging'
    );

    if (!(await isSupported())) {
      console.warn('[web-push] Firebase messaging is not supported in this browser.');
      await requestNotificationPermission();
      return;
    }

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.info('[web-push] Notification permission not granted:', permission);
      return;
    }

    // Register (or reuse) the messaging service worker. Only reached when
    // configured — never at app startup.
    const registration = await navigator.serviceWorker.register(SW_URL);

    // Guard against double-init of the Firebase app.
    const app = getApps().length ? getApp() : initializeApp(firebaseWebConfig);
    const messaging = getMessaging(app);

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      console.warn('[web-push] getToken() returned an empty token.');
      return;
    }

    if (token !== lastRegisteredToken) {
      lastRegisteredToken = token;
      await updateWebPushToken(token);
      console.info('[web-push] Web-push token registered with the server.');
    }

    // Foreground messages: the SW's onBackgroundMessage does not fire while the
    // tab is focused, so surface it ourselves via the registration.
    if (!foregroundHandlerBound) {
      foregroundHandlerBound = true;
      onMessage(messaging, (payload) => {
        const data = (payload.data as Record<string, string> | undefined) ?? {};
        const title = payload.notification?.title ?? data.title ?? 'Mowazaf';
        const body = payload.notification?.body ?? data.body ?? '';
        try {
          registration.showNotification(title, {
            body,
            icon: '/logo192.png',
            badge: '/favicon-32x32.png',
            data: { url: data.url ?? '/' },
          });
        } catch (e) {
          console.warn('[web-push] Failed to show foreground notification:', e);
        }
      });
    }
  } catch (err) {
    console.error('[web-push] Failed to enable web push:', err);
  }
}

/**
 * Clear this browser's web-push token server-side (POST empty string).
 * Best-effort: never throws, so it is safe to await in the sign-out flow.
 */
export async function clearWebPushToken(): Promise<void> {
  lastRegisteredToken = null;
  try {
    await updateWebPushToken('');
  } catch {
    // best-effort — never block sign-out on token cleanup
  }
}
